import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from "axios";
import {
  AppDeleteModal,
  AppModal,
  AppNoInfo,
} from '@/layout/AppComponents';
import { axiosPath } from '../../../../../../routing/helpers/router';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import Translator from '../../../../../components/helpers/Translator';
import InecoRiskManagerRiskGoalCard from './InecoRiskManagerRiskGoalCard';
import InecoRiskManagerRiskKpiCard from './InecoRiskManagerRiskKpiCard';
import InecoRiskManagerRiskGoalForm from './InecoRiskManagerRiskGoalForm';
import InecoRiskManagerRiskKpiForm from './InecoRiskManagerRiskKpiForm';

export default function KPITab({ elementID }) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const kpiTab = useSelector((s) => s.currentPage?.kpiTab?.[elementID]);
  const permissionLevel = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.userBridge?.permissions?.level
  );
  const [isLoading, setIsLoading] = useState(true);
  const [showGoalModal, setShowGoalModal] = useState();
  const [showDeleteGoalModal, setShowDeleteGoalModal] = useState();
  const [showKpiModal, setShowKpiModal] = useState();
  const [
    showDeleteKpiModal,
    setShowDeleteKpiModal,
  ] = useState();

  // > LOAD KPI TAB
  const loadKpi = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_inecoRiskManager_risk_kpi_tab"),
          { moduleBridgeHex, riskID: elementID }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change(
                "kpiTab",
                {
                  [elementID]: data?.goals,
                },
                true
              )
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false)),
    [moduleBridgeHex, elementID]
  );

  useEffect(() => {
    if (isLoading) loadKpi();
  }, [isLoading]);
  // < LOAD KPI TAB

  // > DELETE GOAL
  const handleDeleteGoal = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_inecoRiskManager_risk_goal_delete"),
          {
            moduleBridgeHex,
            riskID: elementID,
            goalID: showDeleteGoalModal,
          }
        )
        .then(() => {
          if (isMounted.current) {
            setShowGoalModal(undefined);
            setShowDeleteGoalModal(undefined);
            setIsLoading(true);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, elementID, showDeleteGoalModal]
  );
  // < DELETE GOAL

  // > DELETE KPI
  const handleDeleteKpi = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_inecoRiskManager_risk_kpi_delete"
          ),
          {
            moduleBridgeHex,
            riskID: elementID,
            kpiID: showDeleteKpiModal,
          }
        )
        .then(() => {
          if (isMounted.current) {
            setShowKpiModal(undefined);
            setShowDeleteKpiModal(undefined);
            setIsLoading(true);
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, elementID, showDeleteKpiModal]
  );
  // < DELETE KPI

  const hasEditPermission = useCallback(() => {
    if (permissionLevel === 0) return true;
  }, [permissionLevel]);

  if (isLoading) return <AppNoInfo minHeight={300} />;

  return (
    <div className="container-fluid">
      {hasEditPermission && (
        <div className="row mt-3">
          <div className="col-12 mb-3">
            <button
              type="button"
              className="btn btn-xs btn-success"
              onClick={() => setShowGoalModal(0)}
            >
              Nueva meta
            </button>
            <AppModal
              show={
                showGoalModal !== undefined &&
                showDeleteGoalModal === undefined
              }
              title={
                showGoalModal === 0
                  ? 'Nueva Meta'
                  : `Editar Meta con ID: ${showGoalModal}`
              }
              size="xl"
              onClose={() => setShowGoalModal(undefined)}
            >
              <InecoRiskManagerRiskGoalForm
                riskID={elementID}
                goalID={showGoalModal}
                kpiTab={kpiTab}
                showDeleteGoalModal={showDeleteGoalModal}
                onShowDeleteGoalModal={setShowDeleteGoalModal}
                onCloseModals={() => {
                  setShowGoalModal(undefined);
                  setShowKpiModal(undefined);
                  setShowDeleteGoalModal(undefined);
                  setShowDeleteKpiModal(undefined);
                }}
                onReloadTab={() => setIsLoading(true)}
              />
            </AppModal>
            <AppDeleteModal
              show={showDeleteGoalModal}
              title={`Eliminar meta con ID ${showGoalModal}`}
              size="lg"
              onClose={() => setShowDeleteGoalModal(undefined)}
              onConfirm={handleDeleteGoal}
            />
          </div>
        </div>
      )}
      {kpiTab?.map((goal) => (
        <div className="row">
          <div className="col-lg-6 mb-3">
            <InecoRiskManagerRiskGoalCard
              goal={goal}
              onShowGoalModal={() => setShowGoalModal(goal?.id)}
              onShowKpiModal={() =>
                setShowKpiModal({ goalID: goal?.id, kpiID: 0 })
              }
            />
          </div>
          <div className="col-lg-6">
            <div className="row">
              {goal?.kpis?.map((k) => (
                <div className="col-12 mb-3">
                  <InecoRiskManagerRiskKpiCard
                    kpi={k}
                    onShowKpiModal={() =>
                      setShowKpiModal({
                        goalID: goal?.id,
                        kpiID: k?.id,
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
      <AppModal
        show={
          showKpiModal !== undefined &&
          showDeleteKpiModal === undefined
        }
        title={
          showKpiModal?.kpiID === 0
            ? 'Nuevo KPI'
            : `Editar KPI con ID: ${showKpiModal?.kpiID}`
        }
        size="xl"
        onClose={() => setShowKpiModal(undefined)}
      >
        <InecoRiskManagerRiskKpiForm
          riskID={elementID}
          goalID={showKpiModal?.goalID}
          kpiID={showKpiModal?.kpiID}
          kpi={kpiTab
            ?.find((g) => g?.id === showKpiModal?.goalID)
            ?.kpis?.find((k) => k?.id === showKpiModal?.kpiID)}
          showDeleteGoalModal={showDeleteGoalModal}
          onShowDeleteKpiModal={setShowDeleteKpiModal}
          onCloseModals={() => {
            setShowGoalModal(undefined);
            setShowKpiModal(undefined);
            setShowDeleteGoalModal(undefined);
            setShowDeleteKpiModal(undefined);
          }}
          onReloadTab={() => setIsLoading(true)}
        />
      </AppModal>
      <AppDeleteModal
        show={showDeleteKpiModal}
        title={`Eliminar KPI con ID ${showDeleteKpiModal}`}
        size="lg"
        onClose={() => setShowDeleteKpiModal(undefined)}
        onConfirm={handleDeleteKpi}
      />
    </div>
  );
}
